import type { ComponentProps } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { plural } from '@frameio/i18n';
import { formatDuration } from '@frameio/shared/transfers/utils';
import { formatBytes } from '@frameio/utils/formatters';
import {
  Box,
  IconButton,
  LinearProgress,
  Text,
  theme,
  Tooltip,
} from '@frameio/vapor';
import {
  IconPauseCircleFill,
  IconPlayCircleFill,
  IconXCircleFill,
} from '@frameio/vapor-icons';
import type { TransferBatch } from '../../types/uploader';
import { getTotalProgress, isEveryJobPaused } from '../../utils';
import TEXT from './generated/TEXT/TotalProgressBar';

TEXT.DEFINE(
  {
    /** Status text showing count of items being transferred (e.g., "5 Items") */
    itemsCount: (count: number): string =>
      plural(count, `${count} Items`, '1 Item'),
    /** Status text showing count of paused items (e.g., "5 Paused Items") */
    pausedItemsCount: (count: number): string =>
      plural(count, `${count} Paused Items`, '1 Paused Item'),
    /** Status label when all transfers are paused */
    paused: 'Paused',
    /** Tooltip and button label to cancel all transfers */
    cancelAll: 'Cancel all',
    /** Tooltip and button label to resume all paused transfers */
    resumeAll: 'Resume all',
    /** Tooltip and button label to pause all transfers */
    pauseAll: 'Pause all',
    /** Separator between status items */
    separator: ' · ',
    /** Transfer speed display (e.g., "1.2 Mb/s") */
    speedPerSecond: (speed: string): string => `${speed}/s`,
  },
  { plural }
);

const ControlButton = forwardRef(
  (props: ComponentProps<typeof IconButton>, ref) => (
    <IconButton ref={ref} variant="ghost" {...props} />
  )
);

export function TotalProgressBar({
  incompleteJobs,
  onCancelAll,
  onPauseAll,
  onResumeAll,
}: {
  incompleteJobs: TransferBatch[];
  onCancelAll?: () => void;
  onPauseAll?: () => void;
  onResumeAll?: () => void;
}) {
  const isPaused = isEveryJobPaused(incompleteJobs);
  const incompleteTasksCount = incompleteJobs.reduce(
    (count, incompleteJob: TransferBatch) => {
      // Do not include isPaused Jobs when there are jobs active.
      if (!isPaused && incompleteJob.isPaused) return count;
      if (!incompleteJob.fileCounts) return count;
      const remainingTasks =
        (incompleteJob.fileCounts.total || 0) -
        (incompleteJob.fileCounts.uploaded || 0);
      return count + remainingTasks;
    },
    0
  );
  const { bytesPerSecond, percentage, remainingTime } =
    getTotalProgress(incompleteJobs);

  return (
    <Container data-testid="total-progress-bar">
      <div className="body">
        <div className="status">
          {isPaused ? (
            <Text
              color="primary"
              data-testid="paused-text"
              display="inline"
              variant="secondary"
            >
              {TEXT.paused}
            </Text>
          ) : (
            <>
              <Text
                color="primary"
                display="inline"
                variant="secondary"
                variantNumeric="tabular-nums"
              >
                <span data-testid="remaining-time">
                  {formatDuration(remainingTime, true)}
                </span>
              </Text>
              <Text
                color="secondary"
                data-testid="size"
                display="inline"
                variant="secondary"
                variantNumeric="tabular-nums"
              >
                {TEXT.separator}
                <span data-testid="incomplete-task-count">
                  {isPaused
                    ? TEXT.pausedItemsCount(incompleteTasksCount)
                    : TEXT.itemsCount(incompleteTasksCount)}
                </span>
                {remainingTime !== Infinity && bytesPerSecond !== 0 && (
                  <>
                    {TEXT.separator}
                    <span data-testid="speed">
                      {TEXT.speedPerSecond(
                        formatBytes(bytesPerSecond, { useBits: true })
                      )}
                    </span>
                  </>
                )}
              </Text>
            </>
          )}
        </div>

        <Box left="15px" position="absolute" right="15px" top="50px">
          <LinearProgress
            aria-label="Progress for all uploads"
            enableGlow={true}
            rounded={true}
            value={percentage}
            variant="gradient"
          />
        </Box>

        <div className="controls">
          {onCancelAll && (
            <Tooltip placement="top" title={TEXT.cancelAll}>
              <ControlButton
                icon={<IconXCircleFill size={24} />}
                label={TEXT.cancelAll}
                onClick={() => onCancelAll()}
              />
            </Tooltip>
          )}

          {isPaused
            ? onResumeAll && (
                <Tooltip placement="top" title={TEXT.resumeAll}>
                  <ControlButton
                    icon={<IconPlayCircleFill size={24} />}
                    label={TEXT.resumeAll}
                    onClick={() => onResumeAll()}
                  />
                </Tooltip>
              )
            : onPauseAll && (
                <Tooltip placement="top" title={TEXT.pauseAll}>
                  <ControlButton
                    icon={<IconPauseCircleFill size={24} />}
                    label={TEXT.pauseAll}
                    onClick={() => onPauseAll()}
                  />
                </Tooltip>
              )}
        </div>
      </div>
    </Container>
  );
}

const FooterTotalProgressHeight = '75px';

const IconContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 48px;
`;

const Container = styled.div`
  display: flex;
  height: ${FooterTotalProgressHeight};
  padding: ${theme.spaces[16]} ${theme.spaces[16]} 0;
  position: relative;

  text-align: center;

  ${IconContainer} {
    margin-right: ${theme.spaces[8]};
  }

  Text {
    margin-bottom: ${theme.spaces[12]};
  }

  .body {
    display: flex;
    flex-grow: 1;

    .status {
      flex-grow: 1;
    }
  }

  .controls {
    align-items: center;
    display: flex;
  }
`;
